/**
 * Map Supabase auth library error strings to user-facing messages.
 *
 * 2026-05-20 (F02-A-025 + F02-A-026 closure): Supabase returns library
 * strings that read as engineering output to fiduciary customers
 * ("Invalid login credentials", "Email rate limit exceeded", postgres
 * RLS messages on MFA-factor failures). Map known prefixes to fiduciary-
 * register copy; fall back to a generic message rather than letting the
 * raw library output reach the screen.
 *
 * Coverage targets:
 *   - signInWithPassword     → "Invalid login credentials"
 *   - signInWithOtp          → "Email rate limit exceeded" (used pre-removal)
 *   - signUp                 → "User already registered"
 *   - resetPasswordForEmail  → "Token has expired or is invalid"
 *   - mfa.enroll             → "MFA factor already exists" / RLS variants
 *
 * Keep this map small and explicit. The fallback is intentionally vague
 * because the alternative is leaking infra strings.
 *
 * 2026-06-25 (BUG-2): the GENERIC fallback now varies by `context` so a signup
 * failure no longer reads "Sign-in failed…" (the wrong verb). Pass
 * `'signup'` from the signup page; the default `'signin'` keeps the login /
 * reset / mfa copy unchanged. The SPECIFIC mapped messages below are
 * context-independent — they read identically in both flows.
 */

/** Which auth flow is calling — selects the generic fallback wording only. */
export type AuthErrorContext = 'signin' | 'signup';

/** The minimal error shape `isTransientAuthError` inspects. Supabase's
 *  AuthError carries a `message` and (often) an HTTP `status`. */
interface TransientAuthErrorInput {
  status?: number;
  message?: string | null;
}

/**
 * BUG-1: classify a caught signup/auth error as TRANSIENT (a blip worth ONE
 * automatic retry) vs TERMINAL (a real, actionable failure that retrying would
 * only mask or worsen).
 *
 * Transient = a connectivity / cold-backend hiccup: a network/fetch failure, a
 * timeout, a no-response abort (status 0), or a gateway/upstream 5xx. Terminal =
 * anything the user must act on (already registered, invalid input, signups
 * disabled) OR a throttle (429 / "rate limit" / "for security purposes…") where a
 * retry would make things worse. Defensive: an unrecognised / empty error is NOT
 * transient — we never blind-retry.
 */
export function isTransientAuthError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as TransientAuthErrorInput;
  const status = typeof e.status === 'number' ? e.status : undefined;
  const msg = (typeof e.message === 'string' ? e.message : '').toLowerCase();

  // A throttle is terminal for auto-retry purposes — retrying worsens it.
  if (status === 429) return false;
  if (msg.includes('rate limit') || msg.includes('for security purposes')) return false;

  // Gateway / upstream / no-response server blips are transient. (429 already
  // returned above; 4xx other than that are terminal — handled by the fallthrough.)
  if (status === 0) return true; // aborted / no response reached us
  if (typeof status === 'number' && status >= 500) return true;

  // Network-layer library strings (no HTTP status reached us).
  if (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('network request failed') ||
    msg.includes('network error') ||
    msg.includes('load failed') ||
    msg.includes('timed out') ||
    msg.includes('timeout') ||
    msg.includes('econnreset') ||
    msg.includes('connection')
  ) {
    return true;
  }

  return false;
}

/**
 * Is this GoTrue's "your session has not passed an MFA challenge" rejection?
 * Observed verbatim on prod 2026-07-29: `401: AAL2 session is required to
 * update email or password when MFA is enabled.` (error_code
 * `insufficient_aal`). Callers use this to OFFER the TOTP step rather than
 * dead-ending the user — see routes/auth/reset-password.tsx.
 */
export function isInsufficientAalError(message: string | undefined | null): boolean {
  const m = (message ?? '').toLowerCase();
  return m.includes('insufficient_aal') || m.includes('aal2');
}

/** Context-specific generic fallback (used for unknown / empty messages). */
function genericFallback(context: AuthErrorContext): string {
  if (context === 'signup') {
    return 'We could not create your account just now. Please try again in a moment, or contact support@elreign.io if it persists.';
  }
  return 'Sign-in failed. Please try again in a moment, or contact support@elreign.io if it persists.';
}

export function mapSupabaseAuthError(
  message: string | undefined | null,
  context: AuthErrorContext = 'signin',
): string {
  if (!message) return genericFallback(context);
  const m = message.toLowerCase();

  if (m.includes('invalid login credentials')) {
    return 'That email and password do not match. If you have just created an account, confirm your email first.';
  }
  if (m.includes('email not confirmed')) {
    return 'Please confirm your email address. Check your inbox for the confirmation link we sent.';
  }
  if (m.includes('rate limit')) {
    return 'You have signed in too many times in a short period. Wait a minute, then try again.';
  }
  if (m.includes('user already registered') || m.includes('already exists')) {
    return 'An account with that email already exists. Sign in instead, or use "Forgot your password" to recover access.';
  }
  if (m.includes('token has expired') || m.includes('token is invalid')) {
    return 'That link or code has expired. Request a fresh one and try again.';
  }
  if (m.includes('row-level security') || m.includes('rls')) {
    // Postgres RLS errors are the worst infrastructure leak. Generic fallback.
    return 'We could not set that up just now. Sign out, sign in again, then retry. If the problem persists, contact your administrator.';
  }
  if (m.includes('password should be at least')) {
    // Keep in lockstep with lib/password-policy.ts (GoTrue enforces 12 —
    // advertising 8 here was the 2026-07-19 miss-hunt "lying mapper" P1).
    return 'Choose a longer password: at least twelve characters with a mix of letters and numbers.';
  }
  if (m.includes('signup is disabled') || m.includes('signups not allowed')) {
    return 'Sign-up is currently restricted. Please contact your trust admin firm for an invitation.';
  }
  if (m.includes('for security purposes')) {
    // Supabase throttle on resetPasswordForEmail / resend.
    return 'For security, please wait a few seconds before requesting another link.';
  }
  if (
    m.includes('invalid totp') ||
    m.includes('totp code') ||
    m.includes('invalid mfa') ||
    (m.includes('code') && m.includes('invalid'))
  ) {
    return 'That code is incorrect or has expired. Open your authenticator app and enter the current 6-digit code.';
  }
  // AAL-1 (2026-07-30, live prod lockout): GoTrue refuses to change a password
  // or email unless the SESSION has passed an MFA challenge. A recovery-link
  // session is only aal1, so every MFA-enrolled user hit this — and the generic
  // "Sign-in failed" gave them nothing to act on. Say what to do instead.
  if (isInsufficientAalError(message)) {
    return 'Enter the 6-digit code from your authenticator app to confirm this change.';
  }

  // Generic fallback — never the raw library string. Wording follows the
  // calling flow so a signup failure does not read "Sign-in failed".
  return genericFallback(context);
}
